import { PayMonthOverviewViewModel, PayMonthStatusEnum } from '@fora-monorepo/employer-my-pages-common/types';
import { getReadableMonthFromPayMonth } from '@fora-monorepo/employer-my-pages-common/utils';
import { Assets, Box, CssGrid, Flex, Text, Tooltip } from '@foradevelopment/fora-ui';
import { componentColors } from '@foradevelopment/design-tokens/nested';

import { TypedTFunction, useTypedTranslation } from '../../i18n/i18n';

type ReportOverviewData = Pick<PayMonthOverviewViewModel, 'payMonth' | 'payMonthStatus'>;

type ReportStatusOverviewProps = {
  data: ReportOverviewData[];
  sortedIndices: number[];
};

export const ReportingStatusOverview = ({ data, sortedIndices }: ReportStatusOverviewProps) => {
  const { t } = useTypedTranslation();
  return (
    <CssGrid gridTemplateColumns={['repeat(2, 1fr)', 'repeat(4, 1fr)', 'repeat(3, 1fr)']} rowGap={10}>
      {sortedIndices.map(index => {
        const { payMonth, payMonthStatus } = data[index];
        const { icon, statusText, moreInfoText } = getPayMonthStatusDetails(payMonthStatus, t);
        const shouldHaveToolTip = payMonthStatus === PayMonthStatusEnum.NoActiveEmployments;
        return (
          <Box
            minHeight={64}
            style={{
              padding: '6px 10px 10px 10px',
              borderBottom: `1px solid ${componentColors.component.color.component.border.divider['level 3']}`,
            }}
            key={index}
          >
            <Flex columnGap={10} alignItems={'center'}>
              <Box alignSelf="flex-start">{icon}</Box>
              <Box>
                <Text marginBottom={3} style={{ fontSize: 14 }} color="subtle">
                  {getReadableMonthFromPayMonth(payMonth)}
                </Text>
                {shouldHaveToolTip ? (
                  <Tooltip
                    content={moreInfoText}
                    id={`tooltip-${payMonth}`}
                    size="small"
                    openOnHover
                    isInTable
                    variant="text"
                  >
                    <Text style={{ fontSize: 14 }}>{statusText}</Text>
                  </Tooltip>
                ) : (
                  <Text style={{ fontSize: 14 }}>{statusText}</Text>
                )}
              </Box>
            </Flex>
          </Box>
        );
      })}
    </CssGrid>
  );
};

const getPayMonthStatusDetails = (status: PayMonthStatusEnum, t: TypedTFunction) => {
  switch (status) {
    case PayMonthStatusEnum.ReportingApproved:
      return {
        icon: <Assets.CircleCheckmarkOutlined color="success" />,
        statusText: t('status.reportingApproved.shortname'),
      };
    case PayMonthStatusEnum.ReportProcessing:
      return {
        icon: <Assets.CircleHourglassIcon color="subtle" />,
        statusText: t('status.reportProcessing.shortname'),
      };
    case PayMonthStatusEnum.ReportWithDeviations:
      return {
        icon: <Assets.CircleExclamationIcon color="burgundy" />,
        statusText: t('status.reportWithDeviations.shortname'),
      };
    case PayMonthStatusEnum.ReportingDelayed:
      return {
        icon: <Assets.CircleExclamationIcon color="burgundy" />,
        statusText: t('status.reportingDelayed.shortname'),
      };
    case PayMonthStatusEnum.NoActiveEmployments:
      return {
        icon: <Assets.CircleCrossIcon color="subtle" />,
        statusText: t('status.noActiveEmployments.shortname'),
        moreInfoText: t('status.noActiveEmployments.description'),
      };
    case PayMonthStatusEnum.ReportingNotDone:
      return {
        icon: <Assets.CircleExclamationIcon color="subtle" />,
        statusText: t('status.reportingNotDone.shortname'),
      };
    default:
      return {
        icon: <Assets.CircleExclamationIcon color="subtle" />,
        statusText: t('status.reportingNotDone.shortname'),
      };
  }
};
